import React, { useRef, useEffect, useCallback, useReducer, useState } from 'react';
import axios, { AxiosInstance } from 'axios';
import { useAuth } from '../auth-provider';
import { Props, AmaV2Values, AmaV2Client, AmaV2Snapshot, ClientsState, SnapshotsState, DescStatsState, AmaV2DescStats, DateLimitersState, AmaV2DateLimiters, AmaV2ActiveSnapshot, AmaV2DescStatsRow } from './ama-v2.types';

const DEFAULT_ACADEMIC_YEAR = '2026';
const SNAPSHOT_LIMIT = 100;
  const ACTIVE_SNAPSHOT_POLL_INTERVAL_MS = 5000;
const CLIENT_DELETE_FALLBACK_ERROR = 'Failed to initiate client deletion. Please try again.';
const DATE_LIMITER_UPDATE_FALLBACK_ERROR = 'Save or recalc request failed';
const SNAPSHOT_DELETE_FALLBACK_ERROR = 'Failed to initiate snapshot deletion. Please try again.';

const AmaV2Context = React.createContext<undefined | AmaV2Values>(undefined);
const Provider = AmaV2Context.Provider;

type ClientsAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: AmaV2Client[] }
  | { type: 'FETCH_ERROR'; error: string }
  | { type: 'SELECT_CLIENT'; clientId: string | null }
  | { type: 'CLEAR_ERROR' };

const clientsReducer = (state: ClientsState, action: ClientsAction): ClientsState => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, status: 'loading', error: null };
    case 'FETCH_SUCCESS': {
      const newData = action.payload;
      const stillSelected = newData.some(c => c.client_id === state.selectedClientId) ? state.selectedClientId : null;
      return {
        status: 'success',
        data: newData,
        selectedClientId: stillSelected,
        selectedClient: stillSelected ? newData.find(c => c.client_id === stillSelected)! : null,
        lastUpdated: new Date().toISOString(),
        error: null,
      };
    }
    case 'FETCH_ERROR':
      return { ...state, status: 'error', error: action.error };
    case 'SELECT_CLIENT': {
      const sel = action.clientId ? state.data.find(c => c.client_id === action.clientId) || null : null;
      return { ...state, selectedClientId: action.clientId, selectedClient: sel };
    }
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

type SnapshotsAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: AmaV2Snapshot[] }
  | { type: 'FETCH_ERROR'; error: string }
  | { type: 'SELECT_SNAPSHOT'; clientId: string | null; academicYear: string | null; snapshotDate: string | null }
  | { type: 'CLEAR_ERROR' };

const snapshotsReducer = (state: SnapshotsState, action: SnapshotsAction): SnapshotsState => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, status: 'loading', error: null };
    case 'FETCH_SUCCESS': {
      const newData = action.payload;
      const key = state.selectedSnapshotKey;
      let selected = null;
      if (key) {
        selected = newData.find(s => s.client_id === key.client_id && s.academic_year === key.academic_year && s.snapshot_date === key.snapshot_date) || null;
      }
      return {
        status: 'success',
        data: newData,
        selectedSnapshotKey: selected ? key : null,
        selectedSnapshot: selected,
        lastUpdated: new Date().toISOString(),
        error: null,
      };
    }
    case 'FETCH_ERROR':
      return { ...state, status: 'error', error: action.error };
    case 'SELECT_SNAPSHOT': {
      if (!action.clientId || !action.academicYear || !action.snapshotDate) {
        return { ...state, selectedSnapshotKey: null, selectedSnapshot: null };
      }
      const key = { client_id: action.clientId, academic_year: action.academicYear, snapshot_date: action.snapshotDate };
      const selected = state.data.find(s => s.client_id === key.client_id && s.academic_year === key.academic_year && s.snapshot_date === key.snapshot_date) || null;
      return { ...state, selectedSnapshotKey: selected ? key : null, selectedSnapshot: selected };
    }
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

function AmaV2Provider({ children }: Props) {

  const { user } = useAuth();
  const axiosInstance = useRef<AxiosInstance | null>(null);
  const abortController = useRef<AbortController>(new AbortController());

  const [clientsState, clientsDispatch] = useReducer(clientsReducer, {
    status: 'idle',
    data: [],
    selectedClientId: null,
    selectedClient: null,
    lastUpdated: null,
    error: null,
  });

  const [snapshotsState, snapshotsDispatch] = useReducer(snapshotsReducer, {
    status: 'idle',
    data: [],
    selectedSnapshotKey: null,
    selectedSnapshot: null,
    lastUpdated: null,
    error: null,
  });

  const [descStatsState, setDescStatsState] = useState<DescStatsState>({
    status: 'idle',
    data: null,
    lastUpdated: null,
    error: null,
  });

  const [dateLimitersState, setDateLimitersState] = useState<DateLimitersState>({
    status: 'idle',
    data: null,
    lastUpdated: null,
    error: null,
  });

  const [selectedAcademicYear, setSelectedAcademicYear] = useState<string>(DEFAULT_ACADEMIC_YEAR);

  // Lookup index for active snapshots keyed by client/year/date
  const [activeSnapshotsIndex, setActiveSnapshotsIndex] = useState<Record<string, AmaV2ActiveSnapshot>>({});

  useEffect(() => {
    if (!user) return; 
    axiosInstance.current = null;
    initAxiosInstance();
  }, [user]);

  useEffect(() => {
    return () => { abortController.current.abort(); };
  }, []);

  useEffect(() => {
    if (!user) return;
    loadSnapshots();    
  }, [selectedAcademicYear]);

  const initAxiosInstance = useCallback(() => {
    if (!user || axiosInstance.current) return;
    axiosInstance.current = axios.create({
      baseURL: process.env.REACT_APP_AMA_V2_API_URL!,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': user.idToken,
      },
    });
  }, [user]);

  // Build active snapshot index whenever clients are refreshed
  useEffect(() => {
    if (clientsState.status !== 'success' || !Array.isArray(clientsState.data)) {
      setActiveSnapshotsIndex({});
      return;
    }

    const makeSnapshotKey = (client_id: string, academic_year: string, snapshot_date: string) =>
      `${client_id}|${academic_year}|${snapshot_date}`;

    const idx: Record<string, AmaV2ActiveSnapshot> = {};
    for (const c of clientsState.data) {
      const act = (c as any).active_snapshot as AmaV2ActiveSnapshot | undefined | null;
      if (!act) continue;
      const { client_id, academic_year, snapshot_date } = act;
      if (!client_id || !academic_year || !snapshot_date) continue;
      const key = makeSnapshotKey(client_id, academic_year, snapshot_date);
      idx[key] = act;
    }
    setActiveSnapshotsIndex(idx);
  }, [clientsState.status, clientsState.data, clientsState.lastUpdated]);

  const handleAxiosError = (err: any): string => {
    if (axios.isAxiosError(err)) {
      const data: any = err.response?.data;
      // Canonical format: { message: string }, fallback to nested context.message
      const msg =
        (typeof data?.message === 'string' ? data.message : null) ||
        (typeof data?.context?.message === 'string' ? data.context.message : null);
      return msg ?? err.message;
    }
    return String(err);
  };

  const isTimeoutLikeError = (err: any): boolean => {
    if (!axios.isAxiosError(err)) return false;

    // No response at all: CORS-masked 504 or genuine network error — request may have reached server
    if (!err.response) return true;

    const status = err.response?.status;
    if (status === 408 || status === 504) return true;

    if (err.code === 'ECONNABORTED') return true;

    const message = (err.message || '').toLowerCase();
    return message.includes('timeout') || message.includes('timed out');
  };

  const normalizeComparableValue = (key: string, value: any): any => {
    if (value === undefined) return undefined;

    if (key === 'notification_recipients') {
      const list = Array.isArray(value)
        ? value
        : typeof value === 'string'
          ? value.split(',')
          : [];
      return list
        .map((item) => String(item).trim())
        .filter((item) => item.length > 0)
        .sort()
        .join(',');
    }

    if (Array.isArray(value)) {
      return value.map((item) => {
        if (typeof item === 'string') return item.trim();
        return item;
      });
    }

    if (typeof value === 'string') return value.trim();

    return value;
  };

  const valueEquals = (left: any, right: any): boolean => {
    if (left === right) return true;

    if (Array.isArray(left) && Array.isArray(right)) {
      if (left.length !== right.length) return false;
      for (let i = 0; i < left.length; i++) {
        if (!valueEquals(left[i], right[i])) return false;
      }
      return true;
    }

    if (
      left !== null &&
      right !== null &&
      typeof left === 'object' &&
      typeof right === 'object'
    ) {
      const leftKeys = Object.keys(left).sort();
      const rightKeys = Object.keys(right).sort();
      if (leftKeys.length !== rightKeys.length) return false;
      for (let i = 0; i < leftKeys.length; i++) {
        if (leftKeys[i] !== rightKeys[i]) return false;
        if (!valueEquals(left[leftKeys[i]], right[rightKeys[i]])) return false;
      }
      return true;
    }

    return false;
  };

  const doesClientContainUpdates = (client: AmaV2Client, updates: Partial<AmaV2Client>): boolean => {
    const entries = Object.entries(updates);

    for (const [key, expectedRaw] of entries) {
      if (expectedRaw === undefined || key === 'client_id') continue;

      const actualRaw = (client as any)[key];
      const expected = normalizeComparableValue(key, expectedRaw);
      const actual = normalizeComparableValue(key, actualRaw);

      if (!valueEquals(actual, expected)) {
        return false;
      }
    }

    return true;
  };

  const fetchClientById = async (client_id: string): Promise<AmaV2Client | null> => {
    if (!user) return null;
    initAxiosInstance();

    try {
      const path = `/clients/${encodeURIComponent(client_id)}`;
      const resp = await axiosInstance.current!.get(path);
      return resp.data?.item || null;
    } catch (err) {
      console.error('AmaV2Provider.fetchClientById :: error', handleAxiosError(err));
      return null;
    }
  };

  const loadClients = async (): Promise<boolean> => {

    if (!user) {
      clientsDispatch({ type: 'FETCH_ERROR', error: 'User not authenticated' });
      return false;
    }

    initAxiosInstance();
    clientsDispatch({ type: 'FETCH_START' });
    let isSuccess = true;

    try {
      const start = performance.now();
      const resp = await axiosInstance.current!.get('/clients', { signal: abortController.current.signal });
      const elapsedMs = Math.round(performance.now() - start);
      const items: AmaV2Client[] = Array.isArray(resp.data?.items) ? resp.data.items : [];
      console.log(`AmaV2Provider.loadClients :: elapsed=${elapsedMs}ms count=${items.length}`);
      if (items.length && clientsState.selectedClientId) {
        const sel = items.find(c => c.client_id === clientsState.selectedClientId);
        if (sel) console.log('AmaV2Provider.loadClients :: selected snapshot', { client_id: sel.client_id, is_active: sel.is_active });
      }
      clientsDispatch({ type: 'FETCH_SUCCESS', payload: items });
    } catch (err: any) {
      isSuccess = false;
      clientsDispatch({ type: 'FETCH_ERROR', error: handleAxiosError(err) });
    }
    return isSuccess;
  };

  const loadSnapshots = async (): Promise<boolean> => {
    if (!user) {
      snapshotsDispatch({ type: 'FETCH_ERROR', error: 'User not authenticated' });
      return false;
    }
    initAxiosInstance();
    snapshotsDispatch({ type: 'FETCH_START' });
    let isSuccess = true;
    try {
      const start = performance.now();
      // Required filter: academicYear (with optional limit)
      const params: any = { academicYear: selectedAcademicYear };
      if (SNAPSHOT_LIMIT !== undefined && SNAPSHOT_LIMIT !== null) {
        params.limit = SNAPSHOT_LIMIT;
      }
      const resp = await axiosInstance.current!.get('/snapshots', {
        params,
        signal: abortController.current.signal,
      });
      const elapsedMs = Math.round(performance.now() - start);
      const items: AmaV2Snapshot[] = Array.isArray(resp.data?.items) ? resp.data.items : [];
      console.log(`AmaV2Provider.loadSnapshots (${selectedAcademicYear}):: elapsed=${elapsedMs}ms count=${items.length}`);
      snapshotsDispatch({ type: 'FETCH_SUCCESS', payload: items });
    } catch (err: any) {
      isSuccess = false;
      snapshotsDispatch({ type: 'FETCH_ERROR', error: handleAxiosError(err) });
    }
    return isSuccess;
  };

  const setSelectedClient = (clientId: string | null) => {
    clientsDispatch({ type: 'SELECT_CLIENT', clientId });
  };

  const clearClientsError = () => clientsDispatch({ type: 'CLEAR_ERROR' });

  const setSelectedSnapshot = (clientId: string | null, academicYear: string | null, snapshotDate: string | null) => {
    snapshotsDispatch({ type: 'SELECT_SNAPSHOT', clientId, academicYear, snapshotDate });
  };

  const clearSnapshotsError = () => snapshotsDispatch({ type: 'CLEAR_ERROR' });

  const addClient = async (client: Partial<AmaV2Client> & { client_id: string }): Promise<boolean> => {
    if (!user) return false;
    initAxiosInstance();
    // Trigger loading state immediately for client mutation
    clientsDispatch({ type: 'FETCH_START' });
    try {
      const { client_id, ...rest } = client;
      console.log('AmaV2Provider.addClient :: request', { client_id, body: rest });
      const resp = await axiosInstance.current!.post(`/clients/${encodeURIComponent(client_id)}`, JSON.stringify(rest), {
        signal: abortController.current.signal,
      });
      console.log('AmaV2Provider.addClient :: response', { status: resp.status, data: resp.data });
      if (resp.status === 201) {
        await loadClients();
        return true;
      }
    } catch (err: any) {
      console.error('addClient error', err);

      const clientId = client.client_id;
      if (isTimeoutLikeError(err) && clientId) {
        const createdClient = await fetchClientById(clientId);
        if (createdClient) {
          console.warn('AmaV2Provider.addClient :: timeout reconciled as success', { client_id: clientId });
          await loadClients();
          return true;
        }

        clientsDispatch({
          type: 'FETCH_ERROR',
          error: 'The save request timed out, and completion could not be confirmed yet. Please refresh before retrying to avoid duplicate clients.',
        });
        return false;
      }

      clientsDispatch({ type: 'FETCH_ERROR', error: handleAxiosError(err) });
    }
    return false;
  };

  const updateClient = async (client_id: string, updates: Partial<AmaV2Client>): Promise<boolean> => {
    if (!user) return false;
    initAxiosInstance();
    // Trigger loading state immediately for client mutation
    clientsDispatch({ type: 'FETCH_START' });
    const previousClient = clientsState.data.find((item) => item.client_id === client_id) || null;
    try {
      // Ensure we don't send mismatched client_id in body
      if (updates.client_id && updates.client_id !== client_id) {
        updates = { ...updates };
        delete updates.client_id;
      }
      console.log('AmaV2Provider.updateClient :: request', { client_id, body: updates });
      const resp = await axiosInstance.current!.put(`/clients/${encodeURIComponent(client_id)}`, JSON.stringify(updates), {
        signal: abortController.current.signal,
      });
      console.log('AmaV2Provider.updateClient :: response', { status: resp.status, data: resp.data });
      if (resp.status === 200) {
        await loadClients();
        return true;
      }
    } catch (err: any) {
      console.error('updateClient error', err);

      if (isTimeoutLikeError(err)) {
        const fetchedClient = await fetchClientById(client_id);
        if (fetchedClient) {
          const updatedAtChanged = Boolean(
            previousClient?.updated_at &&
            fetchedClient.updated_at &&
            previousClient.updated_at !== fetchedClient.updated_at
          );

          const valuesMatch = doesClientContainUpdates(fetchedClient, updates);

          if (updatedAtChanged || valuesMatch) {
            console.warn('AmaV2Provider.updateClient :: timeout reconciled as success', {
              client_id,
              updatedAtChanged,
              valuesMatch,
            });
            await loadClients();
            return true;
          }
        }

        clientsDispatch({
          type: 'FETCH_ERROR',
          error: 'The update request timed out, and completion could not be confirmed yet. Please refresh and verify changes before retrying.',
        });
        return false;
      }

      clientsDispatch({ type: 'FETCH_ERROR', error: handleAxiosError(err) });
    }
    return false;
  };

  const deleteClient = async (client_id: string, cascade = true): Promise<boolean> => {
    
    if (!user) {
      clientsDispatch({ type: 'FETCH_ERROR', error: 'User not authenticated' });
      return false;
    }
    
    initAxiosInstance();
    // Trigger loading state immediately for client mutation
    clientsDispatch({ type: 'FETCH_START' });
    
    try {
      console.log('AmaV2Provider.deleteClient :: request', { client_id, cascade });
      const resp = await axiosInstance.current!.delete(`/clients/${encodeURIComponent(client_id)}`, {
        params: { cascade },
        signal: abortController.current.signal,
      });
      console.log('AmaV2Provider.deleteClient :: response', { status: resp.status });
      if (resp.status === 202 && resp.data?.job_id !== undefined) {
        await loadClients();
        return true;
      }

      clientsDispatch({ type: 'FETCH_ERROR', error: CLIENT_DELETE_FALLBACK_ERROR });
      return false;
    } catch (err: any) {
      const errorMessage = handleAxiosError(err)?.trim() || CLIENT_DELETE_FALLBACK_ERROR;
      console.error('deleteClient error', err);
      clientsDispatch({ type: 'FETCH_ERROR', error: errorMessage });
      return false;
    }
  };

  const addSnapshot = async (client_id: string, academic_year: string, snapshot_date: string, snapshot: Partial<AmaV2Snapshot>): Promise<boolean> => {
    
    if (!user) return false;
    initAxiosInstance();
    // Unified loading state for mutations
    snapshotsDispatch({ type: 'FETCH_START' });
    try {
      const { client_id: bodyClientId, academic_year: bodyAcademicYear, snapshot_date: bodySnapshotDate, ...rest } = snapshot as any;
      if (bodyClientId && bodyClientId !== client_id) delete (rest as any).client_id;
      if (bodyAcademicYear && bodyAcademicYear !== academic_year) delete (rest as any).academic_year;
      if (bodySnapshotDate && bodySnapshotDate !== snapshot_date) delete (rest as any).snapshot_date;
      const path = `/snapshots/${encodeURIComponent(client_id)}/${encodeURIComponent(academic_year)}/${encodeURIComponent(snapshot_date)}`;
      console.log('AmaV2Provider.addSnapshot :: request', { client_id, academic_year, snapshot_date, body: rest });
      const resp = await axiosInstance.current!.post(path, JSON.stringify(rest), { signal: abortController.current.signal });
      console.log('AmaV2Provider.addSnapshot :: response', { status: resp.status, data: resp.data });
      if (resp.status === 201) {
        // await loadSnapshots();
        return true;
      }
    } catch (err: any) {
      console.error('addSnapshot error', err);
      snapshotsDispatch({ type: 'FETCH_ERROR', error: handleAxiosError(err) });
    }
    return false;
  };

  const updateSnapshot = async (client_id: string, academic_year: string, snapshot_date: string, updates: Partial<AmaV2Snapshot>): Promise<boolean> => {
    if (!user) return false;
    initAxiosInstance();
    snapshotsDispatch({ type: 'FETCH_START' });
    try {
      if (updates.client_id && updates.client_id !== client_id) { updates = { ...updates }; delete updates.client_id; }
      if (updates.academic_year && updates.academic_year !== academic_year) { updates = { ...updates }; delete updates.academic_year; }
      if (updates.snapshot_date && updates.snapshot_date !== snapshot_date) { updates = { ...updates }; delete updates.snapshot_date; }
      const path = `/snapshots/${encodeURIComponent(client_id)}/${encodeURIComponent(academic_year)}/${encodeURIComponent(snapshot_date)}`;
      console.log('AmaV2Provider.updateSnapshot :: request', { client_id, academic_year, snapshot_date, body: updates });
      const resp = await axiosInstance.current!.put(path, JSON.stringify(updates), { signal: abortController.current.signal });
      console.log('AmaV2Provider.updateSnapshot :: response', { status: resp.status, data: resp.data });
      if (resp.status === 200) {
        // Let the list page reload its own data when the user returns to the snapshots view.
        return true;
      }
    } catch (err: any) {
      console.error('updateSnapshot error', err);
      snapshotsDispatch({ type: 'FETCH_ERROR', error: handleAxiosError(err) });
    }
    return false;
  };

  const deleteSnapshot = async (client_id: string, academic_year: string, snapshot_date: string): Promise<boolean> => {
    
    if (!user) {
      snapshotsDispatch({ type: 'FETCH_ERROR', error: 'User not authenticated' });
      return false;
    }
    
    initAxiosInstance();

    try {
      const path = `/snapshots/${encodeURIComponent(client_id)}/${encodeURIComponent(academic_year)}/${encodeURIComponent(snapshot_date)}`;
      console.log('AmaV2Provider.deleteSnapshot :: request', { client_id, academic_year, snapshot_date });
      const resp = await axiosInstance.current!.delete(path, { signal: abortController.current.signal });
      console.log('AmaV2Provider.deleteSnapshot :: response', { status: resp.status });

      // Backend now returns 202 for async deletion (instead of 204 for sync)
      if (resp.status === 202 || resp.status === 204) {
        // For 204 (legacy sync), reload immediately
        if (resp.status === 204) {
          await loadSnapshots();
        }
        // For 202 (async), caller will handle polling and reload
        // For both cases, return true to indicate request was accepted
        return true;
      }

      snapshotsDispatch({ type: 'FETCH_ERROR', error: SNAPSHOT_DELETE_FALLBACK_ERROR });
      return false;

    } catch (err: any) {
      const errorMessage = handleAxiosError(err)?.trim() || SNAPSHOT_DELETE_FALLBACK_ERROR;
      console.error('deleteSnapshot error', err);
      snapshotsDispatch({ type: 'FETCH_ERROR', error: errorMessage });
      return false;
    }
  };

  const deleteSnapshotFile = async (client_id: string, academic_year: string, snapshot_date: string): Promise<boolean> => {
    if (!user) return false;
    initAxiosInstance();
    // Use loading state to provide interstitial feedback similar to other mutations
    snapshotsDispatch({ type: 'FETCH_START' });
    try {
      // const url = `/snapshots/${client_id}/${academic_year}/${snapshot_date}/file`;
      const url = `/snapshots/${encodeURIComponent(client_id)}/${encodeURIComponent(academic_year)}/${encodeURIComponent(snapshot_date)}/file`;
      await axiosInstance.current!.delete(url, { signal: abortController.current.signal });
      // After deletion, refresh snapshots to update bronze_path presence
      await loadSnapshots();
      return true;
    } catch (err: any) {
      const message = handleAxiosError(err);
      snapshotsDispatch({ type: 'FETCH_ERROR', error: message });
      return false;
    }
  };  

  const downloadSnapshot = async (client_id: string, academic_year: string, snapshot_date: string): Promise<string | null> => {
    if (!user) {
      console.warn('AmaV2Provider.downloadSnapshot :: user not authenticated');
      return null;
    }

    initAxiosInstance();

    try {
      const response = await axiosInstance.current!.get('/snapshots/download', {
        params: {
          clientId: client_id,
          academicYear: academic_year,
          snapshotDate: snapshot_date,
        },
        signal: abortController.current.signal,
      });

      const downloadUrl = typeof response.data?.download_url === 'string'
        ? response.data.download_url
        : typeof response.data?.downloadUrl === 'string'
          ? response.data.downloadUrl
          : null;

      if (!downloadUrl) {
        const message = response.data?.message || 'Download URL was not returned by the server';
        console.error('AmaV2Provider.downloadSnapshot :: missing download_url', { client_id, academic_year, snapshot_date, response: response.data });
        snapshotsDispatch({ type: 'FETCH_ERROR', error: message });
        return null;
      }

      return downloadUrl;
    } catch (err: any) {
      const message = handleAxiosError(err);
      console.error('AmaV2Provider.downloadSnapshot :: error', { client_id, academic_year, snapshot_date, message });
      snapshotsDispatch({ type: 'FETCH_ERROR', error: message });
      return null;
    }
  };

  const loadDescStats = async (client_id: string, academic_year: string, snapshot_date: string): Promise<boolean> => {
    if (!user) {
      setDescStatsState(s => ({ ...s, status: 'error', error: 'User not authenticated' }));
      return false;
    }
    initAxiosInstance();
    setDescStatsState({ status: 'loading', data: null, lastUpdated: null, error: null });
    try {
      const start = performance.now();
      const path = `/snapshots/${encodeURIComponent(client_id)}/${encodeURIComponent(academic_year)}/${encodeURIComponent(snapshot_date)}/desc-stats`;
      const resp = await axiosInstance.current!.get(path, { signal: abortController.current.signal });
      const elapsedMs = Math.round(performance.now() - start);
      // Backend returns an array of yearly records; support multiple envelope shapes
      const raw = resp?.data;
      const arrayCandidate = Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.items)
          ? raw.items
          : Array.isArray(raw)
            ? raw
            : [];

      const mapped: AmaV2DescStats = mapDescStats(arrayCandidate);
      console.log(`AmaV2Provider.loadDescStats :: elapsed=${elapsedMs}ms years=${mapped.years.length}`);
      setDescStatsState({ status: 'success', data: mapped, lastUpdated: new Date().toISOString(), error: null });
      return true;
    } catch (err: any) {
      const message = handleAxiosError(err);
      setDescStatsState({ status: 'error', data: null, lastUpdated: null, error: message });
      return false;
    }
  };

  const loadDateLimiters = async (client_id: string, academic_year: string, snapshot_date: string): Promise<boolean> => {
    if (!user) {
      setDateLimitersState(s => ({ ...s, status: 'error', error: 'User not authenticated' }));
      return false;
    }
    initAxiosInstance();
    setDateLimitersState({ status: 'loading', data: null, lastUpdated: null, error: null });
    try {
      const start = performance.now();
      const path = `/snapshots/${encodeURIComponent(client_id)}/${encodeURIComponent(academic_year)}/${encodeURIComponent(snapshot_date)}/date-limiters`;
      const resp = await axiosInstance.current!.get(path, { signal: abortController.current.signal });
      const elapsedMs = Math.round(performance.now() - start);
      const data: AmaV2DateLimiters | null = (resp.data && resp.data.data) ? resp.data.data as AmaV2DateLimiters : null;
      const normalized = data ? { ...data, years: Array.isArray(data.years) ? data.years : [], rows: Array.isArray(data.rows) ? data.rows : [], columns: Array.isArray(data.columns) ? data.columns : [] } : null;
      console.log(`AmaV2Provider.loadDateLimiters :: elapsed=${elapsedMs}ms hasData=${data ? 'yes' : 'no'}`);
      setDateLimitersState({ status: 'success', data: normalized, lastUpdated: new Date().toISOString(), error: null });
      return true;
    } catch (err: any) {
      const message = handleAxiosError(err);
      setDateLimitersState({ status: 'error', data: null, lastUpdated: null, error: message });
      return false;
    }
  };

  const clearDescStats = () => {
    setDescStatsState({ status: 'idle', data: null, lastUpdated: null, error: null });
  };

  const clearDateLimiters = () => {
    setDateLimitersState({ status: 'idle', data: null, lastUpdated: null, error: null });
  };

  const clearDateLimitersError = () => {
    setDateLimitersState(state => ({
      ...state,
      status: state.data ? 'success' : 'idle',
      error: null,
    }));
  };

  const updateDateLimiters = async (
    client_id: string,
    academic_year: string,
    snapshot_date: string,
    updates: AmaV2DateLimiters
  ): Promise<boolean> => {
    if (!user) {
      setDateLimitersState(state => ({ ...state, status: 'error', error: 'User not authenticated' }));
      return false;
    }

    initAxiosInstance();
    setDateLimitersState(state => ({ ...state, error: null }));

    try {
      console.log('AmaV2Provider.updateDateLimiters :: request', {
        client_id,
        academic_year,
        snapshot_date,
        payload: updates,
      });

      const path = `/snapshots/${encodeURIComponent(client_id)}/${encodeURIComponent(academic_year)}/${encodeURIComponent(snapshot_date)}/date-limiters`;
      const resp = await axiosInstance.current!.put(path, JSON.stringify(updates), { signal: abortController.current.signal });
      // Backend now returns 202 Accepted with no data; do not mutate provider state here
      if (resp.status === 202) return true;

      setDateLimitersState(state => ({ ...state, status: 'error', error: DATE_LIMITER_UPDATE_FALLBACK_ERROR }));
      return false;
    } catch (err: any) {
      const message = handleAxiosError(err)?.trim() || DATE_LIMITER_UPDATE_FALLBACK_ERROR;
      console.error('AmaV2Provider.updateDateLimiters :: error', message);
      setDateLimitersState(state => ({ ...state, status: 'error', error: message }));
      return false;
    }
  };
  
  const getClient = async (client_id: string): Promise<AmaV2Client | null> => {
    if (!user) {
      console.warn('getClient: user not authenticated');
      return null;
    }

    initAxiosInstance();

    try {
      const path = `/clients/${encodeURIComponent(client_id)}`;
      console.log('AmaV2Provider.getClient :: request', { client_id, path });
      const resp = await axiosInstance.current!.get(path, { signal: abortController.current.signal });
      console.log('AmaV2Provider.getClient :: response', { status: resp.status, data: resp.data });
      
      // Backend returns client in resp.data.item
      const client: AmaV2Client | null = resp.data?.item || null;

      // Update the client in the cached clients list
      if (client) {
        clientsDispatch({
          type: 'FETCH_SUCCESS',
          payload: clientsState.data.map(c => c.client_id === client_id ? client : c)
        });
      }

      return client;
    } catch (err) {
      console.error('AmaV2Provider.getClient :: error', handleAxiosError(err));
      return null;
    }
  };

  const get_active_snapshot = async (client_id: string): Promise<AmaV2ActiveSnapshot | null> => {
    
    if (!user) {
      console.warn('get_active_snapshot: user not authenticated');
      return null;
    }

    initAxiosInstance();

    try {

      const path = `/clients/${encodeURIComponent(client_id)}/active-snapshot`;
      const res = await axiosInstance.current!.get(path, { signal: abortController.current.signal });
     
      const active = res.data?.active_snapshot;
      if (!active) {
        // Clear any existing cache entry for this client
        setActiveSnapshotsIndex((prev) => {
          const next = { ...prev };
          for (const k of Object.keys(next)) {
            if (k.startsWith(`${client_id}|`)) delete next[k];
          }
          return next;
        });
        return null;
      }

      const snapshot: AmaV2ActiveSnapshot = {
        snapshot_date: String(active.snapshot_date),
        is_processing: Boolean(active.is_processing),
        academic_year: String(active.academic_year),
        client_id: String(active.client_id),
        file_path: typeof active.file_path === 'string' ? active.file_path : null,
        desc_stats: active.desc_stats ? mapDescStats(active.desc_stats) : null,
      };

      // Update active snapshot cache for this client
      setActiveSnapshotsIndex((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (k.startsWith(`${snapshot.client_id}|`)) delete next[k];
        }
        const key = `${snapshot.client_id}|${snapshot.academic_year}|${snapshot.snapshot_date}`;
        next[key] = snapshot;
        return next;
      });

      return snapshot;

    } catch (err) {      
      console.error('AmaV2Provider.get_active_snapshot :: error', handleAxiosError(err));
      return null;
    }
  };

  const makeSnapshotKey = (client_id: string, academic_year: string, snapshot_date: string) =>
    `${client_id}|${academic_year}|${snapshot_date}`;

  const isSnapshotActive = (snapshot: AmaV2Snapshot): { isActive: boolean; isProcessing: boolean } => {
    if (!snapshot) return { isActive: false, isProcessing: false };
    const key = makeSnapshotKey(snapshot.client_id, snapshot.academic_year, snapshot.snapshot_date);
    const hit = activeSnapshotsIndex[key];
    return { isActive: !!hit, isProcessing: !!hit?.is_processing };
  };

  const mapDescStats = (data: any): AmaV2DescStats => {
    // Expecting `data` to be an array of records like:
    // [{ year: 2020, applicants: 859, admitted_students: 664, ... }, ...]
    const rowsArray: any[] = Array.isArray(data) ? data : [];

    // Build the ordered list of years (as strings), ascending
    const yearSet = new Set<string>();
    for (const r of rowsArray) {
      if (r && (r.year !== undefined && r.year !== null)) {
        yearSet.add(String(r.year));
      }
    }
    const years = Array.from(yearSet).sort((a, b) => Number(a) - Number(b));

    // Static mapping of backend keys to UI rows
    const mappings: Array<{ key: string; id: string; label: string; type: AmaV2DescStatsRow['type'] }> = [
      { key: 'applicants', id: 'applicants', label: 'Applicants', type: 'number' },
      { key: 'admitted_students', id: 'admitted_students', label: 'Admitted Students', type: 'number' },
      { key: 'admitted_aid_applicants', id: 'admitted_aid_applicants', label: 'Admitted Aid Applicants', type: 'number' },
      { key: 'perc_admits_applying_for_aid', id: 'perc_admits_applying_for_aid', label: '% Admits Applying for Aid', type: 'percent' },
      { key: 'deposits_matrics', id: 'deposits_matrics', label: 'Deposits/Matriculants', type: 'number' },
      { key: 'admit_visits', id: 'admit_visits', label: 'Admit Visits', type: 'number' },
    ];

    // Index rows by year for quick lookup
    const byYear: Record<string, any> = {};
    for (const r of rowsArray) {
      if (!r) continue;
      const y = r.year !== undefined && r.year !== null ? String(r.year) : undefined;
      if (!y) continue;
      byYear[y] = r;
    }

    const rows: AmaV2DescStatsRow[] = mappings.map((m) => {
      const values: Record<string, number> = {};
      for (const y of years) {
        const rec = byYear[y];
        const raw = rec ? rec[m.key] : undefined;
        let num: number;
        if (raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '')) {
          num = Number.NaN; // per requirement: null -> NaN
        } else if (typeof raw === 'number') {
          num = raw;
        } else {
          const parsed = Number(raw);
          num = Number.isFinite(parsed) ? parsed : Number.NaN;
        }
        // Percent values come from backend as decimals (e.g., 0.456) and
        // should display as 45.6% via UI formatting. We keep them as decimals here.
        values[y] = num;
      }
      return { id: m.id, label: m.label, type: m.type, values };
    });

    return {
      years,
      sections: [
        {
          id: 'basePopulation',
          label: 'Base Population',
          rows,
        },
      ],
    };
  }  

  return (
    <Provider value={{
      
      clientsState,      
      loadClients,
      getClient,
      setSelectedClient,
      addClient,
      updateClient,
      deleteClient,
      clearClientsError,

      snapshotsState,      
      loadSnapshots,      
      setSelectedSnapshot,
      addSnapshot,
      updateSnapshot,
      deleteSnapshot,
      deleteSnapshotFile,
      downloadSnapshot,
      clearSnapshotsError,

      selectedAcademicYear,
      setSelectedAcademicYear,
      snapshotLimit: SNAPSHOT_LIMIT,

      descStatsState,
      loadDescStats,
      clearDescStats,
      
      dateLimitersState,
      loadDateLimiters,
      updateDateLimiters,
      clearDateLimiters,
      clearDateLimitersError,
      get_active_snapshot,
      activeSnapshotPollIntervalMs: ACTIVE_SNAPSHOT_POLL_INTERVAL_MS,
      activeSnapshotsIndex,
      isSnapshotActive
    }}>
      {children}
    </Provider>
  );
}

const useAmaV2Context = () => {
  const context = React.useContext(AmaV2Context);
  if (context === undefined) {
    throw new Error('useAmaV2Context must be used within a AmaV2Provider');
  }
  return context;
};

export { AmaV2Provider, useAmaV2Context };